// components/Forum/ThreadCard.tsx
"use client";

import Image from "next/image";
import React, { useState, useRef, useEffect } from "react";
import {
  FiBookmark,
  FiMessageCircle,
  FiMoreHorizontal,
  FiArrowUp,
  FiArrowDown,
  FiFlag,
} from "react-icons/fi";
import clsx from "clsx";

export type ThreadCardProps = {
  id: string;
  title: string;
  body: string;
  author: { name: string; avatarUrl?: string; postCount?: number };
  createdAt?: string;
  bookmarked?: boolean;
  className?: string;
  canEdit?: boolean; // Show edit/delete options only if true

  // moderation
  status?: "PENDING" | "APPROVED" | "REJECTED";
  rejectionReason?: string | null;
  isOwner?: boolean;

  // callbacks
  onEdit?: (id: string) => void;
  onBookmarkToggle?: (id: string, next: boolean) => void;
  onReply?: (id: string) => void;
  onReport?: (id: string) => void;

  // voting + delete
  upvoteCount?: number; // Only show upvote count (never negative)
  userVote?: -1 | 0 | 1;
  onUpvote?: (id: string) => void;
  onDownvote?: (id: string) => void;
  onDelete?: (id: string) => void;

  // loading states
  isDeleting?: boolean;
  isTogglingUpvote?: boolean;
  isTogglingDownvote?: boolean;
  isTogglingSave?: boolean;
};

export default function ThreadCard({
  id,
  title,
  body,
  author,
  createdAt = "",
  bookmarked = false,
  className,
  canEdit = false,
  status = "APPROVED",
  rejectionReason,
  isOwner = false,
  onEdit,
  onBookmarkToggle,
  onReply,
  onReport,
  upvoteCount = 0,
  userVote = 0,
  onUpvote,
  onDownvote,
  onDelete,
  isDeleting = false,
  isTogglingUpvote = false,
  isTogglingDownvote = false,
  isTogglingSave = false,
}: ThreadCardProps) {
  const isPending = status === "PENDING";
  const isRejected = status === "REJECTED";
  const showActions = status === "APPROVED" || isOwner;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const initials =
    author?.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U";

  // Format createdAt as relative time (e.g., "2 hours ago")
  const formatRelativeTime = (dateString: string): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600) {
      const mins = Math.floor(diffInSeconds / 60);
      return `${mins} ${mins === 1 ? "minute" : "minutes"} ago`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    }
    if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? "day" : "days"} ago`;
    }
    if (diffInSeconds < 2592000) {
      const weeks = Math.floor(diffInSeconds / 604800);
      return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    }
    if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000);
      return `${months} ${months === 1 ? "month" : "months"} ago`;
    }
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  };

  const relativeTime = formatRelativeTime(createdAt);

  // Close menu when clicking outside
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    if (menuOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  return (
    <article
      className={clsx(
        "rounded-xl bg-white dark:bg-white/0 backdrop-blur-sm",
        "border shadow-lg dark:shadow-[0_8px_24px_rgba(0,0,0,0.35)]",
        "overflow-hidden",
        isPending && "border-amber-300 dark:border-amber-500/40 opacity-80",
        isRejected && "border-red-300 dark:border-red-500/40 opacity-75",
        !isPending && !isRejected && "border-gray-200 dark:border-white/10",
        className,
      )}
    >
      <div className="p-4 md:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Avatar avatarUrl={author.avatarUrl} initials={initials} />
            <div className="flex flex-col">
              <span className="text-sm font-semibold">{author.name}</span>
              {relativeTime && (
                <span className="text-xs text-gray-500 dark:text-white/50">
                  {relativeTime}
                </span>
              )}
            </div>
          </div>

          {/* Menu - Always show button */}
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="p-2 -mr-2 rounded-full text-gray-600 dark:text-white/80 hover:bg-gray-100 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white"
              aria-label="More options"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <FiMoreHorizontal className="h-5 w-5" />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-48 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-neutral-900/90 backdrop-blur-sm shadow-xl z-20"
                role="menu"
                onMouseLeave={() => setMenuOpen(false)}
              >
                {canEdit ? (
                  <>
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onEdit?.(id);
                      }}
                      disabled={isDeleting}
                    >
                      Edit thread
                    </button>
                    <button
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-white/10 text-red-600 dark:text-red-300 rounded-b-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete?.(id);
                      }}
                      disabled={isDeleting}
                    >
                      {isDeleting && (
                        <div className="h-3 w-3 border-2 border-red-600/30 dark:border-red-300/30 border-t-red-600 dark:border-t-red-300 rounded-full animate-spin" />
                      )}
                      Delete thread
                    </button>
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-white/50">
                    No actions available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Status badges */}
        {isPending && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
            Pending Approval
          </div>
        )}
        {isRejected && (
          <div className="mt-2 space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium">
              Rejected
            </div>
            {rejectionReason && (
              <p className="text-xs text-red-600 dark:text-red-400 pl-1">
                Reason: {rejectionReason}
              </p>
            )}
          </div>
        )}

        {/* Title */}
        <h3 className="mt-3 text-base md:text-lg font-semibold">{title}</h3>

        {/* Body */}
        {body && (
          <p className="mt-2 text-[14px] leading-relaxed text-gray-700 dark:text-white/80">
            {body}
          </p>
        )}

        {/* Footer */}
        {showActions && (
          <div className="mt-3 flex items-center justify-between flex-wrap gap-y-2">
            {/* Left group */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => onUpvote?.(id)}
                className={clsx(
                  "h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed",
                  userVote === 1 && "bg-gray-200 dark:bg-white/10",
                )}
                aria-label="Upvote"
                aria-pressed={userVote === 1}
                disabled={isTogglingUpvote || isTogglingDownvote}
              >
                {isTogglingUpvote ? (
                  <div className="h-3 w-3 border-2 border-gray-300 dark:border-white/30 border-t-gray-900 dark:border-t-white rounded-full animate-spin" />
                ) : (
                  <FiArrowUp className="h-4 w-4" />
                )}
              </button>

              <span className="min-w-6 text-center tabular-nums text-sm">
                {upvoteCount}
              </span>

              <button
                type="button"
                onClick={() => onDownvote?.(id)}
                className={clsx(
                  "h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-white/15 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed",
                  userVote === -1 && "bg-gray-200 dark:bg-white/10",
                )}
                aria-label="Downvote"
                aria-pressed={userVote === -1}
                disabled={isTogglingUpvote || isTogglingDownvote}
              >
                {isTogglingDownvote ? (
                  <div className="h-3 w-3 border-2 border-gray-300 dark:border-white/30 border-t-gray-900 dark:border-t-white rounded-full animate-spin" />
                ) : (
                  <FiArrowDown className="h-4 w-4" />
                )}
              </button>

              <button
                type="button"
                onClick={() => onBookmarkToggle?.(id, !bookmarked)}
                className={clsx(
                  "ml-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition disabled:opacity-50 disabled:cursor-not-allowed",
                  bookmarked
                    ? "border-gray-400 dark:border-white/30 bg-gray-200 dark:bg-white/10"
                    : "border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/40 text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white",
                )}
                aria-pressed={bookmarked}
                aria-label={bookmarked ? "Unsave" : "Save"}
                disabled={isTogglingSave}
              >
                {isTogglingSave ? (
                  <div className="h-3 w-3 border-2 border-gray-300 dark:border-white/30 border-t-gray-900 dark:border-t-white rounded-full animate-spin" />
                ) : (
                  <FiBookmark className="h-4 w-4" />
                )}
              </button>

              {/* Report button - only on approved threads, not own content */}
              {!isOwner && onReport && status === "APPROVED" && (
                <button
                  type="button"
                  onClick={() => onReport(id)}
                  className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 dark:border-white/20 text-sm text-gray-500 dark:text-white/50 hover:text-red-500 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-500/40 transition"
                  aria-label="Report"
                >
                  <FiFlag className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Right group */}
            <button
              type="button"
              onClick={() => onReply?.(id)}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-gray-300 dark:border-white/15 hover:border-gray-400 dark:hover:border-white/30 bg-transparent hover:bg-gray-100 dark:hover:bg-white/5 font-medium transition"
            >
              <FiMessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Add Response</span>
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

/* Avatar */
function Avatar({
  avatarUrl,
  initials,
}: {
  avatarUrl?: string;
  initials: string;
}) {
  return avatarUrl ? (
    <div className="relative h-8 w-8 rounded-full overflow-hidden ring-1 ring-gray-200 dark:ring-white/10">
      <Image
        src={avatarUrl}
        alt="author avatar"
        fill
        className="object-cover"
        sizes="32px"
      />
    </div>
  ) : (
    <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-white/10 flex items-center justify-center text-xs font-semibold ring-1 ring-gray-300 dark:ring-white/10">
      {initials}
    </div>
  );
}
